import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { EMPTY, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import {
  DatePickerComponent,
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
} from '@groundworks/ngx-groundworks-ui';
import {
  AmountSignConstraint,
  EditManualPaymentRequestDto,
  PaymentMethodInfo,
  RecordManualPaymentRequestDto,
} from '../../sdk/sdk';
import { PaymentMethodConstants } from '../../constants/payment-method.constants';
import { ConfigService } from '../../config.service';
import { ModalService } from '../../services/modal.service';
import { PaymentService } from '../../services/payment.service';
import { apiErrorMessage } from '../../utils/api-error-message';
import {
  parseCurrencyAmount,
  rejectAllCurrency,
  requiredNegativeCurrency,
  requiredNonZeroCurrency,
  requiredPositiveCurrency,
} from '../../utils/form-validators';
import { formatLocalIsoDate, formatUtcIsoDate, parseLocalIsoDate } from '../../utils/iso-date';
import { PaginatedModalHeaderComponent } from '../paginated-modal-header/paginated-modal-header.component';

export interface PaymentEditTarget {
  paymentId: string;
  paymentMethodId?: string;
  /**
   * Display name of the stored type, carried so a type the caller cannot see in the methods list
   * can still be shown and re-submitted. Absent for older callers, hence the fallback in
   * applyPrefill.
   */
  paymentMethodName?: string;
  amount: number;
  paymentDate?: Date;
  checkNumber?: string;
}

function localToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

@Component({
  selector: 'app-post-payment-modal',
  imports: [
    ReactiveFormsModule,
    NgSelectComponent,
    DatePickerComponent,
    ModalComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    PaginatedModalHeaderComponent,
  ],
  templateUrl: './post-payment-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PostPaymentModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly formBuilder = inject(FormBuilder);
  private readonly paymentService = inject(PaymentService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly configService = inject(ConfigService);

  readonly paymentMethods = signal<PaymentMethodInfo[]>([]);

  readonly opportunityId = signal<string | null>(null);
  readonly jobId = signal<string | null>(null);
  readonly bcJobNumber = signal<string | null>(null);
  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly showPaymentDatePicker = signal(false);

  readonly editMode = signal(false);
  readonly paymentId = signal<string | null>(null);

  readonly paymentDateMax = computed(() => localToday());

  /**
   * Opens the picker on the first day of the configured backdate window, counting today as day one,
   * so the calendar offers exactly the dates POST/PUT /payments/manual will accept. Zero or negative
   * is clamped to today only, matching the server's own clamp — an empty range would leave the
   * picker with nothing selectable.
   */
  readonly paymentDateMin = computed(() => {
    const today = this.paymentDateMax();
    const days = Math.max(1, this.configService.paymentsBackdateWindowDays);
    return new Date(today.getFullYear(), today.getMonth(), today.getDate() - (days - 1));
  });

  readonly form: FormGroup = this.formBuilder.group({
    paymentType: [null as PaymentMethodInfo | null, Validators.required],
    paymentAmount: [null as number | null],
    paymentDate: [null as string | null],
    checkNumber: [null as string | null],
    notes: [''],
  });

  private readonly selectedPaymentType = toSignal(this.form.controls['paymentType'].valueChanges, {
    initialValue: this.form.controls['paymentType'].value as PaymentMethodInfo | null,
  });

  readonly isCheck = computed(() => this.selectedPaymentType()?.id === PaymentMethodConstants.ManualCheck);
  readonly isMethodSelected = computed(() => this.selectedPaymentType() != null);

  get title(): string {
    return this.editMode() ? 'Edit Payment' : 'Post Payment';
  }

  readonly footerActions = computed<ModalFooterAction[]>(() => [
    {
      label: this.editMode() ? 'Edit Payment' : 'Post Payment',
      type: 'primary',
      action: () => this.confirm(),
      loading: () => this.loading(),
      disabled: () => this.form.invalid || this.loading(),
    },
  ]);

  constructor() {
    this.paymentService.getManualPaymentMethods()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(methods => {
        if (this.editMode()) return;
        this.paymentMethods.set(methods);
        this.applyPrefill();
      });

    this.form.controls['paymentType'].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selected: PaymentMethodInfo | null) => this.onPaymentTypeChanged(selected));
  }

  setEditTarget(target: PaymentEditTarget): void {
    this.editMode.set(true);
    this.paymentId.set(target.paymentId);
    this.editTarget.set(target);

    this.paymentService.getManualPaymentMethods('edit')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(methods => {
        this.paymentMethods.set(methods);
        this.applyPrefill();
      });
  }

  private readonly editTarget = signal<PaymentEditTarget | null>(null);

  private applyPrefill(): void {
    const target = this.editTarget();
    if (!target) return;
    // Restore the stored type as an option rather than bailing: returning here left the whole form
    // blank — type, amount, date and check number — so the user saw an empty Edit Payment modal
    // and, on submitting, silently retyped the payment.
    //
    // The original cause (a gated type filtered out of the caller's methods list) no longer applies
    // to this path: setEditTarget fetches with context=edit, which returns every method ungated, so
    // gated types now resolve normally with their real sign constraint. What is left for
    // resolvePrefillMethod to synthesize is a type that is not a selectable manual method at all.
    const method = this.resolvePrefillMethod(target);
    if (method) {
      this.form.controls['paymentType'].setValue(method);
    }
    this.form.controls['paymentAmount'].setValue(target.amount.toFixed(2), { emitEvent: false });
    this.form.controls['paymentDate'].setValue(
      target.paymentDate ? formatUtcIsoDate(target.paymentDate) : null, { emitEvent: false });
    this.form.controls['checkNumber'].setValue(target.checkNumber ?? null, { emitEvent: false });
    this.form.updateValueAndValidity();
  }

  /**
   * The stored type as a selectable option. Adds it to the list when the caller's own list omits
   * it, so the selection has something to bind to and the type survives an edit that does not
   * change it. Returns null only when the payment carries no type name to show it under, in which
   * case the rest of the form is still prefilled and the user must pick a type explicitly.
   */
  private resolvePrefillMethod(target: PaymentEditTarget): PaymentMethodInfo | null {
    const known = this.paymentMethods().find(m => m.id === target.paymentMethodId);
    if (known) return known;
    if (!target.paymentMethodId || !target.paymentMethodName) return null;

    // The stored type's own sign constraint is not carried on the edit target, so this synthesized
    // option has to state one. Either is the loosest safe guess for a constraint the client could
    // not learn, and the server re-resolves the real one on PUT, so the guess cannot widen what is
    // actually accepted.
    //
    // It governs very little: this branch only runs for a method absent from the ungated edit list,
    // i.e. one that is not a selectable manual method at all — and EditManualPaymentHandler refuses
    // those with a 409 before the amount is looked at. Do not read it as the constraint of an
    // editable type.
    const stored = new PaymentMethodInfo({
      id: target.paymentMethodId,
      name: target.paymentMethodName,
      signConstraint: AmountSignConstraint.Either,
    });
    this.paymentMethods.update(methods => [...methods, stored]);
    return stored;
  }

  private onPaymentTypeChanged(selected: PaymentMethodInfo | null): void {
    this.resetTypeSpecificControls();
    if (selected == null) {
      return;
    }
    this.applySignValidator(selected.signConstraint);
    this.setValidator('paymentDate', Validators.required);
    if (selected.id === PaymentMethodConstants.ManualCheck) {
      this.setValidator('checkNumber', Validators.required);
    }
    if (!this.editMode()) {
      this.defaultPaymentDateToToday();
    }
  }

  private defaultPaymentDateToToday(): void {
    this.form.controls['paymentDate'].setValue(formatLocalIsoDate(this.paymentDateMax()), { emitEvent: false });
  }

  private resetTypeSpecificControls(): void {
    const resettable = this.editMode()
      ? ['checkNumber']
      : ['paymentAmount', 'paymentDate', 'checkNumber'];

    for (const name of resettable) {
      const control = this.form.controls[name];
      control.clearValidators();
      control.setValue(null, { emitEvent: false });
      control.updateValueAndValidity({ emitEvent: false });
    }

    if (!this.editMode()) {
      this.form.controls['notes'].setValue('', { emitEvent: false });
    }
  }

  private static readonly SIGN_REQUIREMENT_MESSAGES: Record<string, string> = {
    [AmountSignConstraint.PositiveOnly]: 'Amount must be positive.',
    [AmountSignConstraint.NegativeOnly]: 'Amount must be negative.',
    [AmountSignConstraint.Either]: 'Amount must be non-zero.',
  };

  /**
   * The sign rule the selected method imposes, so the amount field's message says what is actually
   * wrong. Mirrors AccountCreditRequestModalComponent, which has shown this per-type for ACR since
   * the constraint column existed.
   * <p>
   * Derived rather than assigned: a second signal set from onPaymentTypeChanged went stale when the
   * type was cleared, because that handler returns early on null and edit mode's
   * resetTypeSpecificControls only clears checkNumber — so the amount field kept showing the
   * previous method's requirement.
   * </p>
   */
  readonly selectedSignConstraint = computed(() => this.selectedPaymentType()?.signConstraint);

  paymentAmountErrorMessage(): string {
    const raw = this.form.controls['paymentAmount'].value;
    if (raw == null || raw === '') return 'Amount is required.';
    return this.describeSignRequirement(this.selectedSignConstraint());
  }

  private describeSignRequirement(constraint: AmountSignConstraint | undefined): string {
    if (constraint === undefined) {
      return PostPaymentModalComponent.SIGN_REQUIREMENT_MESSAGES[AmountSignConstraint.Either];
    }
    return PostPaymentModalComponent.SIGN_REQUIREMENT_MESSAGES[constraint]
      ?? 'This amount sign is not supported.';
  }

  private applySignValidator(constraint: AmountSignConstraint | undefined): void {
    this.setValidator('paymentAmount', this.validatorForConstraint(constraint));
  }

  private validatorForConstraint(constraint: AmountSignConstraint | undefined): ValidatorFn {
    switch (constraint) {
      case AmountSignConstraint.PositiveOnly:
        return requiredPositiveCurrency;
      case AmountSignConstraint.NegativeOnly:
        return requiredNegativeCurrency;
      case AmountSignConstraint.Either:
      case undefined:
        return requiredNonZeroCurrency;
      default:
        return rejectAllCurrency;
    }
  }

  private setValidator(controlName: string, validator: ValidatorFn): void {
    const control = this.form.controls[controlName];
    control.setValidators(validator);
    control.updateValueAndValidity({ emitEvent: false });
  }

  formatDateForDisplay(iso: string | null): string {
    if (!iso) return '';
    const [year, month, day] = iso.split('-');
    if (!year || !month || !day) return iso;
    return `${month}/${day}/${year}`;
  }

  paymentDateAsDate(): Date | undefined {
    const iso = this.form.controls['paymentDate'].value as string | null;
    return iso ? parseLocalIsoDate(iso) : undefined;
  }

  togglePaymentDatePicker(): void {
    this.showPaymentDatePicker.update(v => !v);
  }

  handlePaymentDateSelected(date: Date | null): void {
    const control = this.form.controls['paymentDate'];
    control.setValue(date ? formatLocalIsoDate(date) : null);
    control.markAsTouched();
    this.showPaymentDatePicker.set(false);
  }

  onPaymentAmountBlur(): void {
    const control = this.form.controls['paymentAmount'];
    const raw = control.value;
    if (raw == null || raw === '') return;
    const num = parseCurrencyAmount(raw);
    if (!Number.isFinite(num)) {
      control.setValue(null);
      return;
    }
    control.setValue(num.toFixed(2));
  }

  confirm(): void {
    if (this.form.invalid) return;
    const method = this.form.value.paymentType as PaymentMethodInfo | null;
    if (method == null) return;

    if (this.editMode()) {
      this.submitEdit(method);
      return;
    }

    const dto = new RecordManualPaymentRequestDto({
      opportunityId: this.opportunityId() ?? undefined,
      jobId: this.jobId() ?? undefined,
      amount: parseCurrencyAmount(this.form.value.paymentAmount),
      paymentDate: new Date(this.form.value.paymentDate as string),
      paymentMethodId: method.id,
      checkNumber: this.isCheck() ? (this.form.value.checkNumber as string) : undefined,
      notes: this.form.value.notes as string,
    });

    this.submitPayment(
      this.paymentService.recordManualPayment(dto),
      'Failed to record the payment. Please try again.',
    );
  }

  private submitEdit(method: PaymentMethodInfo): void {
    const paymentId = this.paymentId();
    if (paymentId == null) return;

    const dto = new EditManualPaymentRequestDto({
      amount: parseCurrencyAmount(this.form.value.paymentAmount),
      paymentDate: new Date(this.form.value.paymentDate as string),
      paymentMethodId: method.id,
      checkNumber: this.isCheck() ? (this.form.value.checkNumber as string) : undefined,
      notes: this.form.value.notes as string,
    });

    this.errorMessage.set(null);
    this.loading.set(true);
    this.paymentService.editManualPayment(paymentId, dto)
      .pipe(
        catchError(err => this.fail(apiErrorMessage(
          err, 'Failed to edit the payment. Please try again.', 'Manual payment edit failed'))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.activeModal.close(method));
  }

  private submitPayment(operation$: Observable<unknown>, failMessage: string): void {
    this.errorMessage.set(null);
    this.loading.set(true);
    operation$
      .pipe(
        catchError(err => this.fail(apiErrorMessage(err, failMessage, 'Manual payment record failed'))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.closeWithPaymentSuccess());
  }

  private fail(message: string): Observable<never> {
    this.loading.set(false);
    this.errorMessage.set(message);
    return EMPTY;
  }

  private closeWithPaymentSuccess(): void {
    this.activeModal.close(this.form.value.paymentType as PaymentMethodInfo);
    this.modalService.openPaymentSuccessModal(parseCurrencyAmount(this.form.value.paymentAmount), this.bcJobNumber());
  }

  cancel(): void {
    this.activeModal.dismiss();
  }
}
